import apiClient from './apiClient';

const TEMPLATES_URL = '/api/document-templates';

export type DocumentTemplateFormat = 'md' | 'docx' | 'pdf' | 'pptx';

// Formats driven by an uploaded branded file whose placeholders the LLM fills.
export const FILE_TEMPLATE_FORMATS: DocumentTemplateFormat[] = ['docx', 'pdf', 'pptx'];

export const isFileTemplateFormat = (fmt: DocumentTemplateFormat): boolean =>
  FILE_TEMPLATE_FORMATS.includes(fmt);

export interface PlaceholderSpec {
  name: string;
  instruction: string;
}

export interface DocumentTemplateStructure {
  sections?: string[];
  placeholders?: PlaceholderSpec[];
  [key: string]: unknown;
}

export interface DocumentTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  output_format: DocumentTemplateFormat;
  structure: DocumentTemplateStructure | null;
  template_file_name: string | null;
  has_template_file: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentTemplateCreatePayload {
  name: string;
  description?: string | null;
  instructions?: string | null;
  output_format: DocumentTemplateFormat;
  structure?: DocumentTemplateStructure | null;
  is_active?: boolean;
}

export type DocumentTemplateUpdatePayload = Partial<DocumentTemplateCreatePayload>;

const buildFormData = (
  payload: DocumentTemplateCreatePayload | DocumentTemplateUpdatePayload,
  file?: File | null,
): FormData => {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (file) {
    form.append('file', file);
  }
  return form;
};

export const documentTemplateService = {
  list: async (): Promise<DocumentTemplate[]> => {
    const { data } = await apiClient.get<DocumentTemplate[]>(`${TEMPLATES_URL}/`);
    return Array.isArray(data) ? data : [];
  },

  /**
   * List the org's active templates for a given output format (e.g. pptx).
   * Convenience wrapper around `list()` — used by the pptx generation dialog to
   * populate the template picker.
   */
  listByFormat: async (format: DocumentTemplateFormat): Promise<DocumentTemplate[]> => {
    const { data } = await apiClient.get<DocumentTemplate[]>(`${TEMPLATES_URL}/`);
    return (Array.isArray(data) ? data : []).filter(
      (template) => template.output_format === format && template.is_active,
    );
  },

  get: async (id: string): Promise<DocumentTemplate> => {
    const { data } = await apiClient.get<DocumentTemplate>(`${TEMPLATES_URL}/${id}`);
    return data;
  },

  /** Extract placeholder names from an uploaded file (no AI, no persistence). */
  extractPlaceholders: async (
    outputFormat: DocumentTemplateFormat,
    file: File,
  ): Promise<string[]> => {
    const form = new FormData();
    form.append('output_format', outputFormat);
    form.append('file', file);
    const { data } = await apiClient.post<{ output_format: string; placeholders: string[] }>(
      `${TEMPLATES_URL}/extract-placeholders`,
      form,
    );
    return data.placeholders ?? [];
  },

  create: async (
    payload: DocumentTemplateCreatePayload,
    file?: File | null,
  ): Promise<DocumentTemplate> => {
    const { data } = await apiClient.post<DocumentTemplate>(
      `${TEMPLATES_URL}/`,
      buildFormData(payload, file),
    );
    return data;
  },

  update: async (
    id: string,
    payload: DocumentTemplateUpdatePayload,
    file?: File | null,
  ): Promise<DocumentTemplate> => {
    const { data } = await apiClient.put<DocumentTemplate>(
      `${TEMPLATES_URL}/${id}`,
      buildFormData(payload, file),
    );
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`${TEMPLATES_URL}/${id}`);
  },
};

export default documentTemplateService;
